import { useEffect, useState } from "react";
import { Button, Card, ClevisMark, Input, Label } from "@clevis/shared-ui";
import { call } from "../../lib/callFunction";
import { SignaturePad } from "../../components/SignaturePad";
import { textareaClass } from "../../components/JobEditCard";

// The customer's estimate, from the emailed or texted link
// (app.clevis.pro/estimate/{tenantId}/{estimateId}?t={token}): read it, sign
// it, pay the deposit by card when the company takes cards, or say no.
// No account needed; the token is the proof.

interface PublicEstimate {
  companyName: string;
  logoUrl?: string | null;
  companyPhone: string;
  companyEmail: string;
  licenseLines?: string;
  cardFeePct?: number;
  number: number;
  typeName: string;
  customerName: string;
  salespersonName: string;
  lines: Array<{ description: string; quantity: number; unitPriceCents: number | null; amountCents: number | null; includes: string[]; photoUrl?: string }>;
  subtotalCents: number;
  taxCents: number;
  taxRateBps: number;
  totalCents: number;
  notes: string;
  terms: string;
  status: "draft" | "sent" | "accepted" | "declined";
  expired: boolean;
  expiresAt: number | null;
  createdAt: number;
  signerName: string | null;
  signedAt: number | null;
  depositCents: number;
  depositPaidCents: number;
  cardReady: boolean;
}

const money = (c: number) => `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDay = (ms: number) => new Date(ms).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });

export function EstimatePage({ tenantId, estimateId }: { tenantId: string; estimateId: string }) {
  const params = new URLSearchParams(window.location.search);
  const token = params.get("t") ?? "";
  const justPaid = params.get("paid") === "1";
  const [est, setEst] = useState<PublicEstimate | null>(null);
  const [loadError, setLoadError] = useState("");
  const [step, setStep] = useState<"review" | "sign" | "decline">("review");
  const [signerName, setSignerName] = useState("");
  const [signature, setSignature] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function load() {
    try {
      const res = await call<PublicEstimate>("getEstimatePublic", { tenantId, estimateId, token }, { mutation: false });
      setEst(res);
      setSignerName((n) => n || res.customerName);
    } catch (err) {
      setLoadError((err as Error).message);
    }
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, estimateId, token]);
  // Back from Stripe: the deposit is recorded a moment later by the webhook.
  useEffect(() => {
    if (!justPaid) return;
    const t = setTimeout(() => void load(), 4000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [justPaid]);

  async function act(fn: () => Promise<void>) {
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  // Show the outcome right away; the reload that follows only fills in details.
  const sign = () =>
    act(async () => {
      await call("signEstimatePublic", { tenantId, estimateId, token, signerName: signerName.trim(), signaturePngBase64: signature }, { mutation: false });
      setEst((e) => (e ? { ...e, status: "accepted", signerName: signerName.trim(), signedAt: Date.now() } : e));
      setStep("review");
      void load();
    });
  const decline = () =>
    act(async () => {
      await call("declineEstimatePublic", { tenantId, estimateId, token, note }, { mutation: false });
      setEst((e) => (e ? { ...e, status: "declined" } : e));
      setStep("review");
      void load();
    });
  const payDeposit = () =>
    act(async () => {
      const back = `${window.location.origin}${window.location.pathname}?t=${encodeURIComponent(token)}`;
      const res = await call<{ url: string }>("estimateDepositCheckout", { tenantId, estimateId, token, returnUrl: back }, { mutation: false });
      window.location.href = res.url;
    });

  const depositLeft = est ? Math.max(0, est.depositCents - est.depositPaidCents) : 0;
  const contact = est ? [est.companyPhone, est.companyEmail].filter(Boolean).join(" · ") : "";

  return (
    <div className="min-h-full bg-cream">
      <div className="mx-auto max-w-2xl p-4 pb-16">
        <div className="mb-4 flex items-center gap-2 pt-2">
          {est?.logoUrl ? (
            <img src={est.logoUrl} alt={est.companyName} className="max-h-10 max-w-[160px] object-contain" />
          ) : (
            <>
              <ClevisMark size={24} />
              <span className="text-sm font-bold text-ink">{est?.companyName || "Clevis"}</span>
            </>
          )}
          {contact ? <span className="ml-auto text-xs text-steel">{contact}</span> : null}
        </div>
        {est?.licenseLines ? <p className="-mt-3 mb-4 whitespace-pre-line text-[11px] text-steel">{est.licenseLines}</p> : null}

        {loadError ? (
          <Card>
            <p className="py-4 text-center text-sm text-steel">This estimate link isn't valid anymore. Please ask the company to send a new one.</p>
          </Card>
        ) : null}
        {!est && !loadError ? <p className="py-10 text-center text-sm text-steel">Loading your estimate…</p> : null}

        {est ? (
          <>
            {justPaid ? (
              <div className="mb-3 rounded-lg bg-success/10 px-3 py-2 text-sm text-ink">
                Thank you, your deposit went through. {depositLeft > 0 ? "It shows here in a moment." : ""}
              </div>
            ) : null}
            {est.status === "accepted" ? (
              <div className="mb-3 rounded-lg bg-success/10 px-3 py-2 text-sm text-ink">
                ✓ Approved{est.signerName ? ` by ${est.signerName}` : ""}
                {est.signedAt ? ` on ${fmtDay(est.signedAt)}` : ""}. {est.companyName} has a signed copy.
              </div>
            ) : null}
            {est.status === "declined" ? (
              <div className="mb-3 rounded-lg bg-steel/10 px-3 py-2 text-sm text-ink">You declined this estimate. {est.companyName} has been told; reach out to them anytime.</div>
            ) : null}
            {est.status === "sent" && est.expired ? (
              <div className="mb-3 rounded-lg bg-brandorange/10 px-3 py-2 text-sm text-ink">
                This price was good through {est.expiresAt ? fmtDay(est.expiresAt) : "an earlier date"}. Please ask {est.companyName} for an updated estimate.
              </div>
            ) : null}

            <Card className="mb-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-steel">
                    Estimate #{est.number}
                    {est.typeName ? ` · ${est.typeName}` : ""}
                  </p>
                  <h1 className="text-xl font-bold text-ink">{est.companyName}</h1>
                  {est.customerName ? <p className="text-sm text-steel">Prepared for {est.customerName}</p> : null}
                </div>
                <div className="text-right text-xs text-steel">
                  <div>{fmtDay(est.createdAt)}</div>
                  {est.expiresAt && est.status === "sent" && !est.expired ? <div>Price good through {fmtDay(est.expiresAt)}</div> : null}
                  {est.salespersonName ? <div>Your rep: {est.salespersonName}</div> : null}
                </div>
              </div>
              <div className="mt-3 divide-y divide-steel/10">
                {est.lines.map((l, i) => (
                  <div key={i} className="flex items-start justify-between gap-3 py-2">
                    {l.photoUrl ? <img src={l.photoUrl} alt="" className="h-16 w-16 shrink-0 rounded-lg border border-steel/15 object-cover" loading="lazy" /> : null}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-ink">{l.description}</p>
                      {l.quantity !== 1 && l.unitPriceCents !== null ? (
                        <p className="text-xs text-steel">
                          {l.quantity} × {money(l.unitPriceCents)}
                        </p>
                      ) : l.quantity !== 1 ? (
                        <p className="text-xs text-steel">Qty {l.quantity}</p>
                      ) : null}
                      {l.includes.length ? (
                        <div className="mt-0.5 pl-2 text-xs text-steel">
                          {l.includes.map((c, ci) => (
                            <div key={ci}>• {c}</div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                    {l.amountCents !== null ? <p className="shrink-0 text-sm font-medium text-ink">{money(l.amountCents)}</p> : null}
                  </div>
                ))}
              </div>
              <div className="mt-2 space-y-0.5 border-t border-steel/20 pt-2 text-right text-sm">
                <p className="text-xs text-steel">
                  Subtotal {money(est.subtotalCents)} · Tax {money(est.taxCents)}
                </p>
                <p className="text-2xl font-bold text-ink">{money(est.totalCents)}</p>
                {est.depositCents > 0 ? (
                  <p className="text-sm text-ink">
                    Deposit when signed: <span className="font-semibold">{money(est.depositCents)}</span>
                    {est.depositPaidCents > 0 ? <span className="text-success"> · {money(est.depositPaidCents)} received</span> : null}
                  </p>
                ) : null}
              </div>
              {est.notes ? <p className="mt-3 whitespace-pre-wrap text-sm text-steel">{est.notes}</p> : null}
            </Card>

            {est.terms ? (
              <Card className="mb-4">
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-steel">Terms and conditions</p>
                <div className="max-h-72 overflow-y-auto whitespace-pre-wrap text-xs leading-relaxed text-ink">{est.terms}</div>
              </Card>
            ) : null}

            {est.status === "sent" && !est.expired && step === "review" ? (
              <div className="space-y-2">
                <Button className="w-full py-3 text-base" onClick={() => setStep("sign")}>
                  Approve and sign
                </Button>
                <Button variant="ghost" className="w-full" onClick={() => setStep("decline")}>
                  Not right now
                </Button>
              </div>
            ) : null}

            {est.status === "sent" && step === "sign" ? (
              <Card>
                <h2 className="mb-1 text-lg font-bold text-ink">Approve estimate #{est.number}</h2>
                <p className="mb-3 text-sm text-steel">
                  Total {money(est.totalCents)}
                  {est.depositCents > 0 ? `, with a ${money(est.depositCents)} deposit` : ""}.
                </p>
                <div className="mb-3">
                  <Label>Your name</Label>
                  <Input value={signerName} onChange={(e) => setSignerName(e.target.value)} placeholder="Full name" autoComplete="name" />
                </div>
                <SignaturePad onChange={setSignature} />
                {est.terms ? (
                  <label className="mt-3 flex items-start gap-2 text-sm text-ink">
                    <input type="checkbox" className="mt-1" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
                    I approve this work at this price and agree to the terms and conditions above.
                  </label>
                ) : (
                  <p className="mt-3 text-sm text-steel">By signing you approve this work at this price.</p>
                )}
                {error ? <p className="mt-2 text-xs text-danger">{error}</p> : null}
                <div className="mt-4 flex gap-2">
                  <Button variant="ghost" className="flex-1" onClick={() => setStep("review")}>
                    Back
                  </Button>
                  <Button className="flex-1 py-3" busy={busy} disabled={!signature || signerName.trim().length < 2 || (!!est.terms && !agreed)} onClick={() => void sign()}>
                    Sign
                  </Button>
                </div>
              </Card>
            ) : null}

            {est.status === "sent" && step === "decline" ? (
              <Card>
                <h2 className="mb-1 text-lg font-bold text-ink">No problem</h2>
                <p className="mb-2 text-sm text-steel">Want to tell {est.companyName} why? (optional)</p>
                <textarea className={textareaClass} rows={3} maxLength={1000} value={note} placeholder="Went with another option, timing, price…" onChange={(e) => setNote(e.target.value)} />
                {error ? <p className="mt-2 text-xs text-danger">{error}</p> : null}
                <div className="mt-3 flex gap-2">
                  <Button variant="ghost" className="flex-1" onClick={() => setStep("review")}>
                    Back
                  </Button>
                  <Button variant="secondary" className="flex-1" busy={busy} onClick={() => void decline()}>
                    Decline estimate
                  </Button>
                </div>
              </Card>
            ) : null}

            {est.status === "accepted" && depositLeft > 0 ? (
              <Card className="text-center">
                <p className="mb-2 text-sm text-ink">
                  Deposit due: <span className="font-bold">{money(depositLeft)}</span>
                </p>
                {est.cardReady ? (
                  <Button className="w-full py-3 text-base" busy={busy} onClick={() => void payDeposit()}>
                    Pay the deposit by card
                  </Button>
                ) : (
                  <p className="text-sm text-steel">{est.companyName} will contact you about the deposit.</p>
                )}
                {error ? <p className="mt-2 text-xs text-danger">{error}</p> : null}
              </Card>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}
